import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { EntitySelectPage } from '../../../../shared/components/entity-select/entity-select.component';
import { ContactoApiService } from '../../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../../empresas/services/empresa-api.service';
import { DealApiService } from '../../../deals/services/deal-api.service';
import { QuoteApiService } from '../../../quotes/services/quote-api.service';
import { TicketsApiService } from '../../../tickets/services/tickets-api.service';
import { Contact } from '../../../contactos/models/contacto.model';
import { CompanyResponse } from '../../../empresas/models/empresa.model';
import { DealResponse } from '../../../deals/models/deal.model';
import { QuoteResponse } from '../../../quotes/models/quote.model';
import { Ticket } from '../../../tickets/models/tickets.model';

/**
 * Helper reutilizable para los probadores de acciones/eventos de workflows.
 * Encapsula el patrón "buscar registro por nombre" con EntitySelectComponent,
 * conmutando el buscador según el objectType (CONTACT/COMPANY/DEAL/QUOTE/TICKET server-side).
 * Los tipos sin buscador (CUSTOM_OBJECT/SUBSCRIPTION) devuelven lista vacía → usar
 * `isSearchable()` en el template para caer a un input numérico.
 *
 * Uso: instanciar en el componente pasando los services + un getter del objectType
 * y el businessId, y enlazar `search`/`label`/`sublabel` (ya son arrow → conservan `this`).
 */
export class WorkflowRecordSelect {
  constructor(
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private quoteApi: QuoteApiService,
    private ticketsApi: TicketsApiService,
    private getObjectType: () => string | null | undefined,
    private getBusinessId: () => number,
  ) {}

  isSearchable(): boolean {
    const t = this.getObjectType();
    return t === 'CONTACT' || t === 'COMPANY' || t === 'DEAL' || t === 'QUOTE' || t === 'TICKET';
  }

  search = (term: string, page: number): Observable<EntitySelectPage<any>> => {
    const businessId = this.getBusinessId();
    const type = this.getObjectType();
    const empty = of({ items: [] as any[], totalPages: 0 });
    switch (type) {
      case 'CONTACT':
        return this.contactoApi.searchContacts({ businessId, searchTerm: term, page, size: 10 })
          .pipe(map((r) => ({ items: r.content, totalPages: r.totalPages })), catchError(() => empty));
      case 'COMPANY':
        return this.empresaApi.searchCompanies({ businessId, searchTerm: term, page, size: 10 })
          .pipe(map((r) => ({ items: r.content, totalPages: r.totalPages })), catchError(() => empty));
      case 'DEAL':
        return this.dealApi.searchDeals({ businessId, searchName: term, page, size: 10 })
          .pipe(map((r) => ({ items: r.content, totalPages: r.totalPages })), catchError(() => empty));
      case 'QUOTE':
        return this.quoteApi.searchQuotes({ businessId, searchQuoteNumber: term, page, size: 10 })
          .pipe(map((r) => ({ items: r.content, totalPages: r.totalPages })), catchError(() => empty));
      case 'TICKET':
        // quickSearch busca por número (TKT-…) y por asunto, y devuelve hasta 100 sin paginar:
        // se envuelve en una sola página para encajar con el contrato del selector.
        return this.ticketsApi.quickSearchTickets(term)
          .pipe(map((list) => ({ items: list ?? [], totalPages: 1 })), catchError(() => empty));
      default:
        return empty;
    }
  };

  label = (item: any): string => {
    switch (this.getObjectType()) {
      case 'CONTACT': { const c = item as Contact; return `${c.fullname}${c.lastname ? ' ' + c.lastname : ''}`.trim(); }
      case 'COMPANY': { const c = item as CompanyResponse; return c.commercialName || c.businessName || `Empresa #${c.id}`; }
      case 'DEAL':    return (item as DealResponse).name;
      case 'QUOTE':   return (item as QuoteResponse).quoteNumber;
      case 'TICKET':  { const t = item as Ticket; return t.subject || t.ticketNumber || `Ticket #${t.id}`; }
      default:        return String(item?.id ?? '');
    }
  };

  sublabel = (item: any): string => {
    switch (this.getObjectType()) {
      case 'CONTACT': return (item as Contact).mail ?? '';
      case 'COMPANY': return (item as CompanyResponse).businessName ?? '';
      case 'TICKET':  return (item as Ticket).ticketNumber ?? '';
      default:        return '';
    }
  };
}
